import { getCurrentAuthState, isAutomatedTestingUser } from '../Auth.selectors';

import { AUTH_STATES } from '../../constants';
import { DOMAIN } from './Amplify.constants';
import get from 'lodash/get';

/** @returns {typeof import('./Amplify.reducer')['initialState']} */
export const getStore = state => get(state, DOMAIN) || {};

export const getSignInStep = state => {
    const signInStep = get(getStore(state), 'signInStep');
    if (!signInStep) {
        return 'SIGN_IN';
    }
    return signInStep;
};
export const getResetPasswordStep = state => get(getStore(state), 'resetPasswordStep');
export const getAdditionalInfo = state => get(getStore(state), 'additionalInfo', null);
export const getAllowedMFATypes = state => get(getStore(state), 'allowedMFATypes');
export const getAvailableChallenges = state => get(getStore(state), 'availableChallenges');
export const getCodeDeliveryDetails = (state, type = 'SMS') => {
    switch (type) {
        case 'SMS':
        case 'PHONE': {
            return get(getStore(state), 'smsCodeDeliveryDetails', null);
        }
        case 'EMAIL': {
            return get(getStore(state), 'emailCodeDeliveryDetails', null);
        }
    }
    return null;
};
export const getMissingAttributes = state => get(getStore(state), 'missingAttributes', []);

export const isSignedIn = state => get(getStore(state), 'isSignedIn');
export const isPasswordReset = state => get(getStore(state), 'isPasswordReset');
export const isSMSEnabled = state => !!getAllowedMFATypes(state)?.includes('SMS');
export const isTOTPEnabled = state => !!getAllowedMFATypes(state)?.includes('TOTP');

export const getAuthState = state => {
    if (isPasswordReset(state)) {
        const resetPasswordStep = getResetPasswordStep(state);
        switch (resetPasswordStep) {
            case 'DONE':
                return AUTH_STATES.signedIn;
            case 'CONFIRM_PASSWORD_RESET_WITH_CODE':
                return AUTH_STATES.resetPassword;
        }
    }
    const signInStep = getSignInStep(state);
    switch (signInStep) {
        case 'DONE': {
            if (isAutomatedTestingUser(state)) {
                return AUTH_STATES.signedIn;
            }
            // User is signed in, but we don't know what the next step is because we need to enforce MFA even though the UserPool does not require it
            const allowedMFATypes = getAllowedMFATypes(state) ?? [];
            if (allowedMFATypes.length === 0) {
                // User has not setup any MFA
                return AUTH_STATES.selectMFA;
            }
            return AUTH_STATES.signedIn;
        }
        case 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION':
        case 'CONTINUE_SIGN_IN_WITH_MFA_SETUP_SELECTION': {
            const allowedMFATypes = getAllowedMFATypes(state) ?? [];
            if (allowedMFATypes.length === 1) {
                // Only one MFA type is available, use it
                switch (allowedMFATypes[0]) {
                    case 'SMS':
                        return AUTH_STATES.setupPhone;
                    case 'TOTP':
                        return AUTH_STATES.setupTotp;
                    case 'EMAIL':
                        // Should never get here. It means a user does not have an email, and EMAIL is the only MFA type
                        break;
                }
            }
            return AUTH_STATES.selectMFA;
        }
        case 'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION': {
            // User has one or more MFAs setup
            const availableChallenges = getAvailableChallenges(state);
            // 'SMS_MFA' | 'SMS_OTP' | 'SOFTWARE_TOKEN_MFA' | 'EMAIL_OTP' | 'SELECT_MFA_TYPE' | 'SELECT_CHALLENGE' | 'MFA_SETUP' | 'PASSWORD' | 'PASSWORD_SRP' | 'PASSWORD_VERIFIER' | 'CUSTOM_CHALLENGE' | 'DEVICE_SRP_AUTH' | 'DEVICE_PASSWORD_VERIFIER' | 'ADMIN_NO_SRP_AUTH' | 'NEW_PASSWORD_REQUIRED' | 'WEB_AUTHN';
            if (availableChallenges.length === 1) {
                // Only one MFA available, use it
                switch (availableChallenges[0]) {
                    case 'SMS_MFA':
                    case 'SMS_OTP':
                        return AUTH_STATES.verifyPhone;
                    case 'SOFTWARE_TOKEN_MFA':
                        return AUTH_STATES.verifyTOTP;
                    case 'EMAIL_OTP':
                        return AUTH_STATES.verifyEmail;
                    case 'SELECT_CHALLENGE':
                    case 'SELECT_MFA_TYPE':
                        return AUTH_STATES.selectMFA;
                }
            }
            return AUTH_STATES.selectMFA;
        }
        case 'CONTINUE_SIGN_IN_WITH_EMAIL_SETUP': {
            // SHOULD NEVER GET HERE
            break;
        }
        case 'CONFIRM_SIGN_IN_WITH_SMS_CODE': {
            // User has ONLY SMS MFA enabled, prompt for SMS code
            return AUTH_STATES.confirmSignIn;
        }
        case 'CONFIRM_SIGN_IN_WITH_TOTP_CODE': {
            return AUTH_STATES.confirmSignIn;
        }
        case 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP': {
            return AUTH_STATES.setupTotp;
        }
        case 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE': {
            // SHOULD NEVER GET HERE
            return AUTH_STATES.signIn;
        }
        case 'CONFIRM_SIGN_IN_WITH_PASSWORD': {
            // User did not enter their password, prompt for password
            return AUTH_STATES.signIn;
        }
        case 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE': {
            // TODO: If we support Email OTP
            // Auth step requires user to use EMAIL as multifactor authentication by retrieving a code sent to inbox.
            return AUTH_STATES.verifyEmail;
        }
        case 'CONFIRM_SIGN_UP': {
            // SHOULD NOT GET HERE
            // User has signed up but not confirmed their account. We always confirm before user creation
            return AUTH_STATES.signIn;
        }
        case 'RESET_PASSWORD': {
            // User has sent a confirmation code to email (or SMS if using signup via PHONE)
            return AUTH_STATES.resetPassword;
        }
        case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED': {
            // User is forced to set a new password and enter any missing required attributes
            return AUTH_STATES.requireNewPassword;
        }
    }

    return getCurrentAuthState(state);
};
